// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import {
  Square,
  Circle,
  FileImage,
  TreePine,
  Building,
  Armchair,
  Car,
  Trash2,
  Droplets,
  Lightbulb,
  Fence,
  Info,
  DoorOpen,
  Waves,
  MousePointer,
  Hand,
  Pentagon,
  Type,
  MapPin,
  Edit3,
  Ruler,
} from "lucide-react";
import { SVG_GROUP_TEMPLATE } from "./scenarioConfig";

export const optimizeSVG = (svgString) => {
  try {
    return svgString
      .replace(/\s+/g, " ")
      .replace(/>\s+</g, "><")
      .replace(/\s*=\s*"/g, '="')
      .replace(/"\s+/g, '" ')
      .trim();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("SVG optimization failed:", error);
    return svgString;
  }
};

export const generateId = () =>
  `element-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

export const distance = (p1, p2) =>
  Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));

export const LAYERS = {
  BACKGROUND: "background",
  MAIN: "main",
  OVERLAY: "overlay",
};

export const LAYER_CONFIG = {
  [LAYERS.BACKGROUND]: {
    name: "Background",
    icon: FileImage,
    description: "Background images, maps, and floor plans",
    zIndex: 1,
  },
  [LAYERS.MAIN]: {
    name: "Main Objects",
    icon: Square,
    description: "Primary shapes and structures",
    zIndex: 2,
  },
  [LAYERS.OVERLAY]: {
    name: "Overlay",
    icon: TreePine,
    description: "Labels, annotations, and decorative elements",
    zIndex: 3,
  },
};

export const GROUP_TEMPLATE = SVG_GROUP_TEMPLATE;

export const initializeMissingGroups = (svgElement) => {
  if (!svgElement) return;
  const ns = "http://www.w3.org/2000/svg";

  let layer1 = svgElement.querySelector("g#layer1");
  if (!layer1) {
    layer1 = document.createElementNS(ns, "g");
    layer1.setAttribute("id", "layer1");
    svgElement.appendChild(layer1);
  }

  GROUP_TEMPLATE.forEach(([scenarioId, spaceId]) => {
    let scenario = layer1.querySelector(`g#${scenarioId}`);
    if (!scenario) {
      scenario = document.createElementNS(ns, "g");
      scenario.setAttribute("id", scenarioId);
      layer1.appendChild(scenario);
    }

    if (!scenario.querySelector(`g#${spaceId}`)) {
      const space = document.createElementNS(ns, "g");
      space.setAttribute("id", spaceId);
      scenario.appendChild(space);
    }
  });
};

export const pathToPolygon = (pathData, coordinateSystem = "svg") => {
  if (!pathData) return [];

  let sampled = [];
  if (typeof document !== "undefined") {
    try {
      const path = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "path",
      );
      path.setAttribute("d", pathData);
      const total = path.getTotalLength ? path.getTotalLength() : 0;
      if (total > 0) {
        const steps = Math.min(256, Math.max(12, Math.round(total / 8)));
        for (let i = 0; i <= steps; i++) {
          const pt = path.getPointAtLength((total * i) / steps);
          sampled.push({ x: pt.x, y: pt.y });
        }
        if (sampled.length > 1) {
          const first = sampled[0];
          const last = sampled[sampled.length - 1];
          if (Math.hypot(first.x - last.x, first.y - last.y) < 0.01) {
            sampled.pop();
          }
        }
      }
    } catch (e) {
      sampled = [];
    }
  }

  if (sampled.length === 0) {
    const commands = pathData.match(/[ML][\s\d.,-]+/gi) || [];
    commands.forEach((cmd) => {
      const coords = cmd
        .substring(1)
        .trim()
        .split(/[\s,]+/)
        .map(Number)
        .filter((n) => !isNaN(n));
      for (let i = 0; i < coords.length; i += 2) {
        if (coords[i] !== undefined && coords[i + 1] !== undefined) {
          sampled.push({ x: coords[i], y: coords[i + 1] });
        }
      }
    });
  }

  if (coordinateSystem === "osm") {
    return sampled.map((p) => ({ lng: p.x, lat: p.y }));
  }
  return sampled;
};

export const polygonPointsToPath = (points, coordinateSystem = "svg") => {
  if (!points || points.length === 0) return "";
  if (coordinateSystem === "osm") {
    return `M ${points.map((p) => `${p.lng},${p.lat}`).join(" L ")} Z`;
  } else {
    return `M ${points.map((p) => `${p.x},${p.y}`).join(" L ")} Z`;
  }
};

export const PARK_OBJECTS = {
  tree: {
    icon: TreePine,
    label: "Tree/Shrub",
    defaultLayer: LAYERS.OVERLAY,
    fill: "#4caf50",
    stroke: "#2e7d32",
    geometry: {
      radius: 18,
      trunkWidth: 6,
      trunkHeight: 22,
      labelOffset: 28,
    },
  },
  stage: {
    icon: Building,
    label: "Stage/Platform",
    defaultLayer: LAYERS.MAIN,
    fill: "#8d6e63",
    stroke: "#5d4e37",
  },
  seating: {
    icon: Armchair,
    label: "Seating/Bench",
    defaultLayer: LAYERS.MAIN,
    fill: "#6d4c41",
    stroke: "#3e2723",
  },
  parking: {
    icon: Car,
    label: "Parking Lot",
    defaultLayer: LAYERS.BACKGROUND,
    fill: "#9e9e9e",
    stroke: "#424242",
  },
  toilet: {
    icon: Building,
    label: "Toilet/Restroom",
    defaultLayer: LAYERS.MAIN,
    fill: "#2196f3",
    stroke: "#0d47a1",
  },
  trash: {
    icon: Trash2,
    label: "Trash/Recycling",
    defaultLayer: LAYERS.OVERLAY,
    fill: "#ff9800",
    stroke: "#e65100",
    geometry: {
      radius: 18,
      trashWidth: 12,
      trashHeight: 18,
      labelOffset: 26,
    },
  },
  fountain_drink: {
    icon: Droplets,
    label: "Drinking Fountain",
    defaultLayer: LAYERS.OVERLAY,
    fill: "#00bcd4",
    stroke: "#006064",
    geometry: {
      radius: 18,
      cupTopWidth: 20,
      cupBottomWidth: 12,
      cupHeight: 22,
      labelOffset: 26,
    },
  },
  fountain_water: {
    icon: Waves,
    label: "Water Feature",
    defaultLayer: LAYERS.MAIN,
    fill: "#03a9f4",
    stroke: "#01579b",
  },
  lighting: {
    icon: Lightbulb,
    label: "Lighting Fixture",
    defaultLayer: LAYERS.OVERLAY,
    fill: "#ffeb3b",
    stroke: "#f57f17",
    geometry: {
      radius: 18,
      poleWidth: 3,
      poleHeight: 26,
      lampRadius: 9,
      labelOffset: 30,
    },
  },
  fence: {
    icon: Fence,
    label: "Fence/Gate",
    defaultLayer: LAYERS.MAIN,
    fill: "#795548",
    stroke: "#3e2723",
  },
  info_board: {
    icon: Info,
    label: "Information Board",
    defaultLayer: LAYERS.OVERLAY,
    fill: "#9c27b0",
    stroke: "#4a148c",
    geometry: {
      radius: 18,
      boardWidth: 24,
      boardHeight: 16,
      labelOffset: 26,
    },
  },
  entrance: {
    icon: DoorOpen,
    label: "Access Point/Entrance",
    defaultLayer: LAYERS.MAIN,
    fill: "#4caf50",
    stroke: "#1b5e20",
  },
};

export const tools = [
  { id: "select", icon: MousePointer, label: "Select" },
  { id: "drag", icon: Hand, label: "Drag" },
  { id: "rectangle", icon: Square, label: "Rectangle" },
  { id: "circle", icon: Circle, label: "Circle" },
  { id: "polygon", icon: Pentagon, label: "Polygon" },
  { id: "text", icon: Type, label: "Text" },
  { id: "pin", icon: MapPin, label: "Pin" },
  { id: "edit", icon: Edit3, label: "Edit Points" },
  { id: "ruler", icon: Ruler, label: "Measure (Ruler)" },
];

export const CANVAS_WIDTH = 800;
export const CANVAS_HEIGHT = 600;
export const GRID_SIZE = 20;

export const DEFAULT_VIEWBOX = { x: 400, y: 250, width: 1600, height: 1000 };

/** Parse "x y width height" (DB `svg_viewbox` or SVG viewBox attribute). */
export const parseViewBoxString = (value) => {
  if (!value) return null;
  if (
    typeof value === "object" &&
    value.width != null &&
    value.height != null
  ) {
    return {
      x: Number(value.x) || 0,
      y: Number(value.y) || 0,
      width: Number(value.width) || DEFAULT_VIEWBOX.width,
      height: Number(value.height) || DEFAULT_VIEWBOX.height,
    };
  }
  if (typeof value !== "string") return null;
  const parts = value
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  if (parts.length < 4 || parts.some((n) => !Number.isFinite(n))) return null;
  const [x, y, width, height] = parts;
  if (width <= 0 || height <= 0) return null;
  return { x, y, width, height };
};

/** Serialize viewBox for the `svg_viewbox` DB column. */
export const formatViewBoxString = (viewBox) => {
  const vb = parseViewBoxString(viewBox) || DEFAULT_VIEWBOX;
  return `${vb.x} ${vb.y} ${vb.width} ${vb.height}`;
};

/** Resolve viewBox from DB field, saved SVG, or fallback default. */
export const resolveViewBox = ({
  svgViewbox,
  svgContent,
  fallback = DEFAULT_VIEWBOX,
} = {}) => {
  const fromDb = parseViewBoxString(svgViewbox);
  if (fromDb) return fromDb;

  if (typeof svgContent === "string" && svgContent.trim()) {
    try {
      const doc = new DOMParser().parseFromString(svgContent, "image/svg+xml");
      const attr = doc.documentElement?.getAttribute("viewBox");
      const fromSvg = parseViewBoxString(attr);
      if (fromSvg) return fromSvg;
    } catch {
      // ignore malformed SVG
    }
  }

  return { ...fallback };
};

/** Read svg_viewbox from API payloads that may use snake_case or camelCase. */
export const getScenarioSvgViewbox = (scenario) =>
  scenario?.svg_viewbox ?? scenario?.svgViewbox ?? null;
